/**
 * GameCanvas — orchestrator.
 *
 * Controls:
 *   WASD / Arrows  drive
 *   SPACE          fire missile
 *   SHIFT          rocket boost
 *   E              trunk monkey
 *   V              toggle MAP / COCKPIT view
 *   M              mute / unmute
 *
 * Features:
 *   - PvP weapon relay via WS (missile / monkey / took_damage)
 *   - Server-authoritative pickup (via Race.jsx pickup_attempt → pickup_result)
 *   - Random cartoon pedestrians; collision costs coins (WS hit_pedestrian → server)
 *   - Map view (top-down cartoon) + Cockpit view (pseudo-3D driver POV)
 *   - Per-city theme + difficulty + grace period
 *   - Sound FX (engine, missile, monkey, pickup, reject, hit, win)
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import sfx from "@/lib/sfx";
import { drawArtifact, ARTIFACT_RADIUS } from "@/components/game/itemVisuals";
import { initPedestrians, updatePedestrians, drawPedestrian } from "@/components/game/pedestrians";
import { renderCockpit } from "@/components/game/cockpit";
import { drawCar, drawBoostFlames, drawDustPuffs, drawEnemy, drawMissile, drawMonkeyBlast } from "@/components/game/characters";
import { drawSky, drawGrass, drawRoads, drawObstacle, drawMood, drawGraceBanner, drawNameTag, drawHpBar } from "@/components/game/scenery";
import { drawCityProps, drawCityGroundBuildings, getPropCount, findNearbyPropIndex } from "@/components/game/cityScenes";
import { drawRiddleBoss, isPlayerNearBoss, BOSS_X, BOSS_Y } from "@/components/game/riddleBoss";
import { drawCityLandmark } from "@/components/game/landmarks";
import { spawnATeamVan, updateATeamVan, drawATeamVan } from "@/components/game/ateamVan";
import { tickGang, drawGangVan, gangStruggle, isPlayerStolen, struggleProgress } from "@/components/game/gangs";
import { initKnightFlail, updateKnightFlail, drawKnightFlail } from "@/components/game/knightFlail";
import { shouldTriggerCopSteal, applyCopSteal, drawStolenCopCar, drawInvincibleBadge } from "@/components/game/copSteal";
import { updateSidekick, drawSidekick } from "@/components/game/sidekick";
import {
  ULTIMATE_LABELS,
  updateUltimateReadiness,
  fireUltimate,
  updateActiveUltimate,
  drawActiveUltimate,
} from "@/components/game/ultimates";
import { drawJailCellMarker, drawJailOverlay } from "@/components/game/jail";
import { drawMiniMapHUD } from "@/components/game/minimap";

const WORLD_W = 1280;
const WORLD_H = 820;

// Internal pixel resolution of the canvas. We render at half the logical world
// size and let CSS upscale with nearest-neighbor for chunky pixel art.
const PIX_SCALE = 0.5;
const CANVAS_W = Math.round(WORLD_W * PIX_SCALE);
const CANVAS_H = Math.round(WORLD_H * PIX_SCALE);

// Camera zoom: how much closer the in-race camera is than a full-world view.
// 2x means we see roughly half the world width/height, focused on the player.
const CAMERA_ZOOM = 2.4;
const CLAMP = (v, min, max) => Math.max(min, Math.min(max, v));

export default function GameCanvas({
  city,
  car,
  localUserId,
  onItemTouched,
  onFinish,
  onHpChange,
  onWeaponsChange,
  onItemsChange,
  onPedestrianHit,     // () => main page sends WS hit_pedestrian + shows floating -50
  onMonkeyDeploy,      // () => main page triggers the Trunk-Monkey cinematic overlay
  onTourismProgress,   // ({visited,total}) => HUD updates "Tourist X/Y" widget
  onJailChange,        // ({active}) => Race shows "Sell artifact" escape button
  onGangChange,        // ({state}) — fires when the carjacker van transitions
                       //   between idle/chasing/stolen/fleeing so Race can toast.
  onRouteEdgeCross,    // ('right'|'left') => Race fetches the next route + reloads
  onBossEncounter,     // () => Race opens the riddle modal
  viewMode,            // "map" | "cockpit"
  onViewModeChange,    // (next) => void — allow canvas to toggle
  remotePlayers = {},
  sendEvent,
  registerRemoteHandlers,
  registerPickupResolver,
  registerCanvasActions, // ({ onCorrectQuiz, sellItemToEscape }) — bound by Race.jsx
  sidekickDesign,        // optional { template, body_color, accent_color, name, decals }
  catchphrase,           // string|null — popped above the car on correct quiz
  onUltimateChange,      // ({ready, used, label}) — HUD subscriber
  onUltimateRejected,    // (reason) — fired when player presses U/Enter
                         // but the ultimate isn't ready / already used.
  copStealStatus,        // ({available, races_until_next}) — used to gate the S+Shift trigger
  onCopStealTrigger,     // () => Race fires POST /cop_steal/use, on 200 calls applyCopSteal
  onOilSlick,            // () => Race toast: "🛢️ OIL SLICK!" once per deploy
  paused = false,
}) {
  const canvasRef = useRef(null);
  const difficulty = city.difficulty || { cops: 2, fbi: 1, enemy_speed: 1, grace_seconds: 5 };
  const theme = city.theme || { bg_top: "#6FA8DC", bg_bottom: "#C3D9E9", road: "#8E8E8E", stripe: "#F5B71B", mood: "industrial", accent_glow: "#FF3B30" };

  const stateRef = useRef(null);
  if (!stateRef.current) {
    stateRef.current = buildInitialState(city, car, difficulty);
  }
  const [, forceRender] = useState(0);

  // Preload the AI-generated skyline image once. The canvas uses it as the
  // sky layer instead of the hand-drawn shapes when ready.
  const skylineImgRef = useRef(null);
  useEffect(() => {
    if (!city.skyline_url) { skylineImgRef.current = null; return; }
    const img = new Image();
    img.onload = () => { skylineImgRef.current = img; };
    img.onerror = (e) => {
      console.warn("[skyline] failed to load", img.src, e);
      skylineImgRef.current = null;
    };
    img.src = city.skyline_url.startsWith("http")
      ? city.skyline_url
      : `${process.env.REACT_APP_BACKEND_URL}${city.skyline_url}`;
  }, [city.skyline_url]);

  // Mirror the catchphrase prop into the state ref each render so the
  // renderMap closure can read it without crashing on stale references.
  useEffect(() => {
    stateRef.current.catchphraseText = catchphrase || null;
  }, [catchphrase]);

  // Spawn the sidekick AI car when the player saved a design pre-race.
  // Spawned just behind/right of the player so it's visible immediately.
  useEffect(() => {
    if (!sidekickDesign) return;
    const st = stateRef.current;
    const p = st.player;
    st.sidekick = {
      x: p.x - 60, y: p.y + 36,
      vx: 0, vy: 0,
      angle: 0,
      hp: 100, max_hp: 100,
      design: sidekickDesign,
      mode: "follow", // "follow" | "ram" | "jailbreak"
      target: null,
      lastRam: 0,
      jailBreakDone: false,
    };
  }, [sidekickDesign]);

  // ---- keyboard ----
  useEffect(() => {
    const st = stateRef.current;
    const down = (e) => {
      const k = e.key.toLowerCase();
      st.keys[k] = true;
      if ([" ", "arrowup", "arrowdown", "arrowleft", "arrowright"].includes(k)) e.preventDefault();
      if (e.key === " ") fireMissile();
      if (k === "e") {
        // While carjacked, E mashes the struggle meter to escape faster.
        if (isPlayerStolen(st)) {
          gangStruggle(st);
          return;
        }
        deployMonkey();
      }
      if (k === "u" || e.key === "Enter") {
        // Skip when the user is typing into an input (catchphrase setter,
        // sidekick name field, etc.) — Enter in a text input shouldn't
        // burn their once-per-race ultimate.
        const tag = (e.target?.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) {
          return;
        }
        // ULTIMATE move — once per race, only when cops have been gone 5s
        const fired = fireUltimate(st, car.kind || "cruiser");
        if (fired) {
          // Push state to HUD immediately (the per-frame check sees `used`)
          onUltimateChange?.({
            ready: false,
            used: true,
            label: ULTIMATE_LABELS[(car.kind || "cruiser")] || "ULTIMATE",
          });
        } else {
          // Tell the player WHY nothing fired (used vs not-ready). Without
          // this the silent rejection felt like the key wasn't bound.
          onUltimateRejected?.(
            st.ultimateUsed ? "used" : "not_ready",
          );
        }
      }
      if (e.key === "Shift") {
        // Plain Shift = boost. The cockpit toggle is button-only now to keep
        // Shift-boost reliable and prevent accidents from Ctrl-modifier holds.
        boost();
      }
      // Shift + O = deploy oil slick (cops within radius spin in circles).
      // Detected on the 'o' keydown so we can read e.shiftKey reliably;
      // checking on the Shift keydown wouldn't know which letter is held.
      if (k === "o" && e.shiftKey) {
        e.preventDefault();
        deployOilSlick();
      }
      if (k === "m") sfx.setMuted(!sfx.muted);
    };
    const up = (e) => { st.keys[e.key.toLowerCase()] = false; };
    window.addEventListener("keydown", down, { passive: false });
    window.addEventListener("keyup", up);
    sfx.init();
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      sfx.stop();
    };
  }, [viewMode, onViewModeChange]); // eslint-disable-line

  // ---- remote WS handlers ----
  useEffect(() => {
    registerRemoteHandlers?.({
      onRemoteMissile: (m) => {
        stateRef.current.remoteMissiles.push({
          id: m.missile_id, attacker_id: m.user_id,
          x: m.x, y: m.y, vx: m.vx, vy: m.vy, life: 90,
        });
      },
      onRemoteMonkey: (m) => {
        stateRef.current.remoteMonkeys.push({
          attacker_id: m.user_id, x: m.x, y: m.y, r: 10, maxR: 140, life: 40,
        });
      },
      onPlayerDamaged: ({ user_id, hp }) => {
        if (user_id === localUserId) stateRef.current.player.hp = hp;
        else stateRef.current.remoteHp[user_id] = hp;
      },
    });
  }, [registerRemoteHandlers, localUserId]);

  // ---- pickup resolver ----
  useEffect(() => {
    registerPickupResolver?.((itemId, correct) => {
      const st = stateRef.current;
      const it = st.items.find((x) => x.id === itemId);
      if (it?.collected) {
        st.pickupLocked = false;
        return;
      }
      if (it && correct) {
        it.collected = true;
      } else if (it) {
        // Wrong answer (or dismissed) — set cooldown so the modal doesn't
        // immediately re-trigger while the player is still standing on the
        // artifact. Cooldown clears when they drive out of range.
        it.cooldown = true;
      }
      st.pickupLocked = false;
      if (correct) sfx.pickup(); else sfx.reject();
      onItemsChange?.(st.items.filter((x) => x.collected).length);
    });
  }, [registerPickupResolver, onItemsChange]);

  // ---- weapons ----
  const fireMissile = () => {
    const st = stateRef.current;
    if (paused || (st.weapons.missile ?? 0) <= 0) return;
    st.weapons.missile -= 1;
    onWeaponsChange?.({ ...st.weapons });
    const p = st.player;
    const mid = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const missile = {
      id: mid, attacker_id: localUserId,
      x: p.x + Math.cos(p.angle) * 24, y: p.y + Math.sin(p.angle) * 24,
      vx: Math.cos(p.angle) * 9, vy: Math.sin(p.angle) * 9, life: 90,
    };
    st.missiles.push(missile);
    sfx.missile();
    sendEvent?.({ type: "fire_missile", missile_id: mid, x: missile.x, y: missile.y, vx: missile.vx, vy: missile.vy });
  };
  const boost = () => {
    const st = stateRef.current;
    if (paused || (st.weapons.rocket_boost ?? 0) <= 0) return;
    st.weapons.rocket_boost -= 1;
    onWeaponsChange?.({ ...st.weapons });
    st.boostUntil = performance.now() + 1800;
  };
  const deployMonkey = () => {
    const st = stateRef.current;
    if (paused || (st.weapons.monkey_bomb ?? 0) <= 0) return;
    const p = st.player;
    const now = performance.now();
    // JAILBREAK: pressing E while jailed launches the Trunk Monkey to
    // smash the cell open. Consumes 1 monkey but instantly ends jail.
    if (p.jailedUntil > now) {
      st.weapons.monkey_bomb -= 1;
      onWeaponsChange?.({ ...st.weapons });
      // Big breakout flash at the jail position
      st.monkeys.push({ attacker_id: localUserId, x: p.x, y: p.y, r: 14, maxR: 200, life: 50 });
      sfx.monkey();
      // End jail this frame; updatePlayer's release branch will respawn safely.
      p.jailedUntil = now - 1;
      onMonkeyDeploy?.();
      return;
    }

    // Normal deployment — AoE blast around the player. Damage to enemies is
    // applied inside updateMonkeys (so re-entered enemies eat damage too).
    st.weapons.monkey_bomb -= 1;
    onWeaponsChange?.({ ...st.weapons });
    st.monkeys.push({ attacker_id: localUserId, x: p.x, y: p.y, r: 10, maxR: 140, life: 40 });
    sfx.monkey();
    sendEvent?.({ type: "deploy_monkey", x: p.x, y: p.y });
    // Fire the cinematic overlay — Race.jsx handles the zoom-in animation.
    onMonkeyDeploy?.();
  };

  // Oil slick — drop a black puddle behind the player that spins any cop
  // (or FBI) that touches it for 3 s. Cools down for 8 s between uses.
  // User feedback: "hit shift O and an oil slick comes out of my car and
  // all the cop cars spin in circles."
  const deployOilSlick = () => {
    const st = stateRef.current;
    if (paused) return;
    const now = performance.now();
    if (now < (st.oilSlickReadyAt || 0)) return; // cooling down
    if (st.player.jailedUntil > now) return;     // not while in jail
    // Drop the slick BEHIND the car in the direction it's traveling so
    // cops on the player's tail are the natural target.
    const p = st.player;
    const speed = Math.hypot(p.vx || 0, p.vy || 0);
    let bx = p.x, by = p.y;
    if (speed > 0.4) {
      bx = p.x - (p.vx / speed) * 22;
      by = p.y - (p.vy / speed) * 22;
    }
    st.oilSlicks.push({
      id: `slick_${now.toFixed(0)}`,
      x: bx,
      y: by,
      r: 46,
      createdAt: now,
      expiresAt: now + 6000,   // slick persists 6s on the road
      usedCops: new Set(),     // each cop spun at most once per slick
    });
    st.oilSlickReadyAt = now + 8000; // 8s cooldown between drops
    sfx.monkey(); // reuse the existing low whoomp SFX
    // Feedback toast — Race.jsx listens via onOilSlick.
    onOilSlick?.();
  };

  // Imperative actions exposed to Race.jsx for things only Race knows about
  // (correct quiz count → A-Team van, sell-item-to-escape jail).
  useEffect(() => {
    // Stash the boss-encounter callback in canvas state so the physics
    // loop can fire it without re-rendering.
    if (stateRef.current) stateRef.current._onBossEncounter = onBossEncounter;
    // Debug helpers — exposed on window for Playwright/devtools verification.
    // Production-safe: only exposes harmless teleport + jail-trigger hooks.
    if (typeof window !== "undefined") {
      window.__GP_DEBUG__ = {
        jumpToRightEdge: () => {
          const st = stateRef.current;
          if (!st) return;
          st.player.x = 1240;
          st.player.vx = 3;
        },
        jumpToLeftEdge: () => {
          const st = stateRef.current;
          if (!st) return;
          st.player.x = 40;
          st.player.vx = -3;
        },
        jumpToBoss: () => {
          // Teleport the player right next to the boss (route 1 only). The
          // physics-loop boss-collision check fires on the next frame.
          const st = stateRef.current;
          if (!st) return;
          st.player.x = 380; // boss x
          st.player.y = 410;
          st.player.vx = 0; st.player.vy = 0;
        },
        forceArrest: () => {
          const st = stateRef.current;
          if (!st) return;
          st.player.hp = 0;
        },
        // Force-trigger the carjacker gang to spawn next frame regardless
        // of the 25 s spawn timer. The van will steer toward the player +
        // contact-freeze on touch as in production.
        spawnGang: () => {
          const st = stateRef.current;
          if (!st) return;
          st.gang = { state: "idle", used: false, nextSpawnAt: 0 };
        },
        // Force-trigger the carjacked state (skips the chase). For visual
        // verification of the HUD overlay + struggle meter.
        forceCarjack: () => {
          const st = stateRef.current;
          if (!st) return;
          st.gang = {
            state: "stolen",
            used: true,
            stolenAt: performance.now(),
            struggleProgress: 0,
            heldX: st.player.x,
            heldY: st.player.y,
            van: {
              x: st.player.x + 30,
              y: st.player.y + 30,
              vx: 0, vy: 0, angle: 0,
            },
          };
        },
        // Force-fire the cop-steal trigger (skips S+Shift detection).
        // Snapshots the closest cop and calls the local applyCopSteal.
        // Useful for Playwright tests where holding multiple keys at the
        // same exact frame is unreliable.
        forceCopSteal: () => {
          const st = stateRef.current;
          if (!st) return;
          // Find the closest live cop and snapshot the index.
          let best = -1, bestD = 1e9;
          for (let i = 0; i < (st.enemies || []).length; i++) {
            const e = st.enemies[i];
            if (!e || e.type !== "cop") continue;
            if (st.destroyedEnemyIdx?.has(i)) continue;
            const d = Math.hypot(e.x - st.player.x, e.y - st.player.y);
            if (d < bestD) { bestD = d; best = i; }
          }
          if (best < 0) return false;
          st._copStealTargetIdx = best;
          applyCopSteal(st);
          return true;
        },
        getState: () => stateRef.current,
      };
    }
    registerCanvasActions?.({
      onCorrectQuiz: () => {
        const st = stateRef.current;
        st.correctQuizCount += 1;
        if (st.correctQuizCount >= 2) spawnATeamVan(st);
        // Pop the player's catchphrase as a speech bubble for ~2.5 s.
        if (st.catchphraseText) {
          st.catchphraseUntil = performance.now() + 2500;
        }
      },
      sellItemToEscape: () => {
        const st = stateRef.current;
        const p = st.player;
        if (p.jailedUntil <= performance.now()) return false;
        // Find the most recently collected (or any collected) item, undo it.
        const collected = st.items.filter((i) => i.collected);
        if (collected.length === 0) return false;
        const idx = st.items.indexOf(collected[collected.length - 1]);
        st.items[idx].collected = false;
        st.items[idx].cooldown = false;
        // End jail
        p.jailedUntil = performance.now() - 1;
        onItemsChange?.(st.items.filter((x) => x.collected).length);
        return true;
      },
      // Swap to a new route's items + obstacles + spawn position. Called by
      // Race.jsx after fetching /api/cities/{id}?route=N when an edge is
      // crossed. Preserves COLLECTED items so the player keeps their progress.
      loadRoute: ({ items, obstacles, fromDir, routeIndex, riddleBossName, riddleVariant }) => {
        const st = stateRef.current;
        const collectedIds = new Set(st.items.filter((i) => i.collected).map((i) => i.id));
        st.items = items.map((i) => ({ ...i, collected: collectedIds.has(i.id) }));
        st.obstacles = [...obstacles];
        // Spawn the player on the OPPOSITE edge to where they exited.
        // Reset Y to mid-map so the boss zone is on the natural drive path.
        if (fromDir === "right") {
          st.player.x = 60;
          st.player.y = WORLD_H / 2;
          st.player.angle = 0; // face right
        } else if (fromDir === "left") {
          st.player.x = WORLD_W - 60;
          st.player.y = WORLD_H / 2;
          st.player.angle = Math.PI; // face left
        }
        st.player.vx = 0; st.player.vy = 0;
        st._edgeCrossLatch = false;
        st._edgeCrossDir = null;
        st._edgeCrossFired = false;
        st._edgeCrossCooldownUntil = performance.now() + 2500;
        st._riddleRouteIndex = routeIndex ?? null;
        st._riddleBossName = riddleBossName ?? "BOSS";
        // Each route has its own boss — reset the solved flag every swap so
        // the second-route boss appears even after route-1 boss is defeated.
        st._riddleSolved = false;
        // Boss visual variant index — 0 for route 1's first boss, 1 for
        // route 2's championship boss.
        st._riddleVariant = riddleVariant ?? 0;
      },
      // Boss riddle solved — flip the gate flag so the boss disappears.
      markRiddleSolved: () => {
        const st = stateRef.current;
        st._riddleSolved = true;
        st._riddleTriggered = false;
      },
      // Cop steal — called by Race.jsx after the /cop_steal/use POST
      // returns 200. Flips the player into the invulnerable cop-cruiser
      // mode for the rest of THIS race.
      applyCopSteal: () => {
        const st = stateRef.current;
        applyCopSteal(st);
      },
      // Cop steal trigger was rejected by the server (cooldown not yet
      // elapsed) — clear the latch so a future S+Shift hold can retry.
      copStealRejected: () => {
        const st = stateRef.current;
        st._copStealRequestedAt = null;
      },
      // Returns the sidekick's current state for the Wingman-badge check
      // at /race/finish time. Race.jsx forwards `survived` + `deployed`
      // to the backend so the badge can be awarded server-side.
      getSidekickStatus: () => {
        const st = stateRef.current;
        const sk = st?.sidekick;
        return {
          deployed: !!sk,
          survived: !!sk && (sk.hp ?? 0) > 0,
        };
      },
    });
  }, [registerCanvasActions, onItemsChange, onBossEncounter]);

  // ---- main loop ----
  const loop = useCallback((ctx) => {
    const st = stateRef.current;
    if (!paused) {
      const elapsed = (performance.now() - st.startedAt) / 1000;
      const remaining = 380 - elapsed;
      if (!st.finishedFlag && (remaining <= 0 || allCollected(st))) {
        st.finishedFlag = true;
        onFinish?.({
          items_collected: st.items.filter((i) => i.collected).length,
          time_seconds: Math.min(380, elapsed),
          finished: allCollected(st),
          tourism_visited: st.visitedProps.size,
          tourism_target: getPropCount(st.cityId),
        });
        if (allCollected(st)) sfx.win();
      }
      const graceOver = elapsed >= (difficulty.grace_seconds ?? 5);
      const jailed = st.player.jailedUntil > performance.now();
      updatePlayer(st);
      // After updatePlayer may have set _jailFired, surface to React.
      if (st.player._jailFired) {
        const fired = st.player._jailFired;
        st.player._jailFired = null;
        onJailChange?.({ active: fired === "arrested" });
      }
      // Edge-cross — fire callback once per latch then keep flag set until
      // Race.jsx tells us the new route is loaded (via canvasActions.loadRoute).
      if (st._edgeCrossLatch && st._edgeCrossDir && !st._edgeCrossFired) {
        st._edgeCrossFired = true;
        onRouteEdgeCross?.(st._edgeCrossDir);
      }
      updateMissiles(st, sendEvent);
      updateMonkeys(st, sendEvent);
      updateOilSlicks(st);
      updateATeamVan(st);
      // Skip carjacker logic entirely while invulnerable (cop steal active),
      // while jailed (you shouldn't be jacked from inside a cell — see
      // user feedback "I can get carjacked when I am in the jail"),
      // or while the gang is mid-spawn-wait.
      if (!st.player.invulnerable && !jailed) tickGang(st);
      // Cop-car steal — fire one-shot when the player holds S + Shift
      // simultaneously near a cop. Race.jsx receives the trigger event,
      // posts to the backend, and on success calls canvasActions.applyCopSteal.
      if (!st.player.copStolen && !st._copStealRequestedAt && shouldTriggerCopSteal(st, copStealStatus)) {
        st._copStealRequestedAt = performance.now();
        onCopStealTrigger?.();
      }
      // Knight Hauler signature weapon — flail. Lazy-init the first time
      // the loop runs for a Knight; no-op for other chassis.
      if ((car.kind || "cruiser") === "knight") {
        initKnightFlail(st);
        updateKnightFlail(st, sendEvent);
      }
      // Emit a single event each time the gang state transitions so Race
      // can show "🚐 CARJACKED!" / "💪 Got the car back!" toasts.
      if (st.gang && st.gang.state !== st._gangPrev) {
        const prev = st._gangPrev;
        st._gangPrev = st.gang.state;
        if (prev !== undefined) onGangChange?.({ state: st.gang.state });
      }
      updateSidekick(st);
      updateUltimateReadiness(st);
      updateActiveUltimate(st);
      // Push readiness changes to the HUD via the onUltimateChange callback.
      if (st.ultimateReady !== st._ultPrev) {
        st._ultPrev = st.ultimateReady;
        onUltimateChange?.({
          ready: st.ultimateReady,
          used: st.ultimateUsed,
          label: ULTIMATE_LABELS[(car.kind || "cruiser")] || "ULTIMATE",
        });
      }
      if (!jailed) {
        updateEnemies(st, graceOver, difficulty.enemy_speed ?? 1);
        updatePedestrians(st.pedestrians, st.player, () => {
          sfx.hit();
          onPedestrianHit?.();
        });
        checkItemPickup(st, onItemTouched);
      }

      // --- Tourism Bonus: track props the player has driven near ----------
      // findNearbyPropIndex is O(props) per frame — props are tiny (<= 5 per
      // city), so this is negligible. Each prop index is added to the Set
      // exactly once.
      if (st.cityId) {
        const idx = findNearbyPropIndex(st.cityId, st.player.x, st.player.y, 40);
        if (idx >= 0 && !st.visitedProps.has(idx)) {
          st.visitedProps.add(idx);
          onTourismProgress?.({
            visited: st.visitedProps.size,
            total: getPropCount(st.cityId),
          });
        }
      }

      const speed = Math.hypot(st.player.vx, st.player.vy) / 6;
      sfx.engine(speed);

      const now = performance.now();
      if (now - st.lastBroadcast > 100) {
        st.lastBroadcast = now;
        sendEvent?.({
          type: "pos", x: st.player.x, y: st.player.y, angle: st.player.angle,
          body_color: car.body_color, accent_color: car.accent_color, kind: car.kind || "cruiser",
        });
      }
    } else {
      sfx.stopEngine();
    }

    if (viewMode === "cockpit") {
      // Cockpit view doesn't use a follow-camera (it's already first-person).
      ctx.setTransform(PIX_SCALE, 0, 0, PIX_SCALE, 0, 0);
      renderCockpit(ctx, st, remotePlayers, st.player, theme, st.pedestrians, skylineImgRef.current);
    } else {
      // === CAMERA FOLLOW + ZOOM ===
      // Reset transform, then build a follow-camera that keeps the player
      // centered while zooming in by CAMERA_ZOOM. Camera is CLAMPED to the
      // world edges so the player never sees a black void outside the map —
      // when they're near a corner, the camera stops scrolling and they
      // simply approach the screen edge.
      const totalScale = PIX_SCALE * CAMERA_ZOOM;
      const viewW = CANVAS_W / totalScale; // visible world units (X)
      const viewH = CANVAS_H / totalScale; // visible world units (Y)
      const camCX = Math.min(Math.max(st.player.x, viewW / 2), WORLD_W - viewW / 2);
      const camCY = Math.min(Math.max(st.player.y, viewH / 2), WORLD_H - viewH / 2);
      const tx = CANVAS_W / 2 - camCX * totalScale;
      const ty = CANVAS_H / 2 - camCY * totalScale;
      ctx.setTransform(totalScale, 0, 0, totalScale, tx, ty);
      renderMap(ctx, st, remotePlayers, car, theme, difficulty, skylineImgRef.current, camCX);
      ctx.setTransform(PIX_SCALE, 0, 0, PIX_SCALE, 0, 0);
      drawMiniMapHUD(ctx, st);

      // Hit flash — red vignette pulse when a cop just rammed the player.
      const flash = st.player.hitFlashUntil - performance.now();
      if (flash > 0) {
        ctx.save();
        ctx.globalAlpha = Math.min(0.55, flash / 250 * 0.55);
        ctx.fillStyle = "#FF3B30";
        ctx.fillRect(0, 0, CANVAS_W * PIX_SCALE, CANVAS_H * PIX_SCALE);
        ctx.restore();
      }
      // Strike counter — when the player has 1 or 2 hits, show a small
      // warning so they know the next ram = jail.
      if (st.player.hitsTaken > 0 && st.player.jailedUntil <= performance.now()) {
        ctx.save();
        ctx.font = "bold 11px 'Press Start 2P', monospace";
        ctx.textAlign = "center";
        ctx.lineWidth = 4;
        ctx.strokeStyle = "#000";
        ctx.fillStyle = st.player.hitsTaken >= 2 ? "#FF3B30" : "#FFCC00";
        const txt = `STRIKES ${st.player.hitsTaken}/3`;
        const w = CANVAS_W * PIX_SCALE;
        ctx.strokeText(txt, w / 2, 28);
        ctx.fillText(txt, w / 2, 28);
        ctx.restore();
      }
      const jl = st.player.jailedUntil - performance.now();
      if (jl > 0) {
        const canMonkey = (st.weapons.monkey_bomb ?? 0) > 0;
        const canSell = st.items.some((i) => i.collected);
        drawJailOverlay(ctx, st.player, Math.ceil(jl / 1000), canMonkey, canSell, car);
      }

      // Carjacked HUD — struggle meter + "MASH E TO ESCAPE" prompt.
      // Drawn in logical 1280×820 coords (matching jail overlay) so it
      // covers the full canvas regardless of camera zoom.
      if (isPlayerStolen(st)) {
        const w = 1280;
        const h = 820;
        ctx.save();
        // Dim the background a touch so the HUD pops.
        ctx.fillStyle = "rgba(0,0,0,0.45)";
        ctx.fillRect(0, 0, w, h);
        // Banner
        ctx.font = "bold 32px 'Press Start 2P', monospace";
        ctx.textAlign = "center";
        ctx.lineWidth = 8;
        ctx.strokeStyle = "#000";
        ctx.fillStyle = "#FF3B30";
        ctx.strokeText("CARJACKED!", w / 2, h / 2 - 60);
        ctx.fillText("CARJACKED!", w / 2, h / 2 - 60);
        ctx.font = "bold 18px 'Press Start 2P', monospace";
        ctx.fillStyle = "#FFCC00";
        ctx.strokeText("PRESS E 5 TIMES TO ESCAPE", w / 2, h / 2 - 18);
        ctx.fillText("PRESS E 5 TIMES TO ESCAPE", w / 2, h / 2 - 18);
        // Struggle bar
        const bw = 480, bh = 28;
        const bx = (w - bw) / 2;
        const by = h / 2 + 14;
        ctx.fillStyle = "#000";
        ctx.fillRect(bx - 4, by - 4, bw + 8, bh + 8);
        ctx.fillStyle = "#222";
        ctx.fillRect(bx, by, bw, bh);
        const pct = struggleProgress(st);
        ctx.fillStyle = pct > 0.66 ? "#22C55E" : pct > 0.33 ? "#FFCC00" : "#FF3B30";
        ctx.fillRect(bx, by, bw * pct, bh);
        ctx.restore();
      }
    }
  }, [paused, onFinish, onItemTouched, onPedestrianHit, onTourismProgress, onGangChange, copStealStatus, onCopStealTrigger, remotePlayers, sendEvent, car, theme, difficulty, localUserId, viewMode]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    // NOTE: per-frame setTransform inside the loop builds the follow-camera.
    // We don't set a global transform here — the loop owns it.
    let raf;
    const tick = () => { loop(ctx); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [loop]);

  useEffect(() => {
    const t = setInterval(() => {
      onHpChange?.(stateRef.current.player.hp);
      forceRender((x) => x + 1);
    }, 200);
    return () => clearInterval(t);
  }, [onHpChange]);

  return (
    <div className="relative w-full h-full">
      <canvas
        ref={canvasRef}
        width={CANVAS_W}
        height={CANVAS_H}
        data-testid="race-canvas"
        className="w-full h-full block"
        style={{ background: theme.bg_top, imageRendering: "pixelated" }}
      />
    </div>
  );
}

// ======================================================================================
// Game logic (same physics as before)
// ======================================================================================
function buildInitialState(city, car, difficulty) {
  // Cops + FBI now have their own HP — missiles + Trunk Monkey destroy them.
  // Each enemy also gets a `cornerOffset` angle so the pack approaches the
  // player from different directions instead of all stacking on one tile.
  const copCount = (difficulty.cops ?? 2) + 1; // +1 so they can corner
  const fbiCount = difficulty.fbi ?? 1;
  const cops = Array.from({ length: copCount }).map((_, i) => ({
    type: "cop",
    x: 200 + i * 240,
    y: 120 + (i % 2) * 100,
    vx: 0, vy: 0, angle: 0,
    hp: 60, max_hp: 60,
    cornerOffset: (i / copCount) * Math.PI * 2,
    disabled: 0,         // ms timestamp; while > now, enemy is stunned & won't pursue
    bounceCooldown: 0,
  }));
  const fbis = Array.from({ length: fbiCount }).map((_, i) => ({
    type: "fbi",
    x: 1000 + (i % 2) * 80,
    y: 700 - i * 120,
    vx: 0, vy: 0, angle: 0,
    hp: 90, max_hp: 90,
    cornerOffset: Math.PI + (i / Math.max(1, fbiCount)) * Math.PI,
    disabled: 0,
    bounceCooldown: 0,
  }));
  return {
    cityId: city.id,
    player: {
      x: 120, y: WORLD_H - 120, vx: 0, vy: 0, angle: -Math.PI / 2,
      hp: car.hp ?? 100, max_hp: car.max_hp ?? 100,
      speed_level: car.speed_level ?? 1, armor_level: car.armor_level ?? 1,
      jailedUntil: 0,
      jailOriginX: 0,
      jailOriginY: 0,
      gateBoostUntil: 0, // ms timestamp; while > now, tires glow + speed +50%
      hitsTaken: 0,      // counts cop ramming hits; 3+ hits sends you to jail
      hitFlashUntil: 0,  // brief red-flash on the screen after a hit
    },
    weapons: { ...(car.weapons ?? { missile: 3, rocket_boost: 2, monkey_bomb: 1 }) },
    keys: {},
    items: city.items.map((i) => ({ ...i, collected: false })),
    obstacles: [...city.obstacles],
    // Speed-boost gates — Sonic-style checker rings scattered around the
    // track. Pre-positioned per city to land on driving lanes.
    gates: makeGatesForCity(city.id),
    missiles: [], remoteMissiles: [],
    monkeys: [], remoteMonkeys: [],
    remoteHp: {},
    boostUntil: 0,
    enemies: [...cops, ...fbis],
    pedestrians: initPedestrians(),
    startedAt: performance.now(),
    finishedFlag: false,
    pickupLocked: false,
    lastBroadcast: 0,
    // Tourism tracking — set of distinct prop indices the player has driven
    // within ~40 px of. Sent to /race/finish for the Local Legend bonus.
    visitedProps: new Set(),
    // A-Team van rescue: a one-time-per-race scripted vehicle that appears
    // after `correctQuizCount` >= 2 and barrels through cops, knocking them
    // away. `aTeamVan` is set when the rescue is triggered and animated by
    // the loop until it leaves the map.
    correctQuizCount: 0,
    aTeamVan: null, // { x, y, vx, vy, life, hits: Set<copIdx> }
    aTeamUsed: false,
    // Carjacker gang — initialised lazily inside tickGang on first frame.
    // The state-transition emitter compares against `_gangPrev`; seeding it
    // to "idle" makes sure the very first transition (idle → chasing) fires
    // a clean event instead of being treated as the initial frame.
    gang: null,
    _gangPrev: "idle",
    // Explosion VFX particles spawned when a missile/monkey-bomb DESTROYS
    // a cop or FBI. Lifetime ~90 frames; rendered as expanding fire ring +
    // smoke puffs + flying debris.
    explosions: [],
    // Sidekick AI car — populated when sidekickDesign is provided. Drives
    // alongside the player, rams cops, and on jail breaks the bars.
    sidekick: null,
    // ULTIMATE move state — once-per-race chassis special. Arms 2 s
    // after race start; player presses U / Enter to fire.
    ultimateArmedAt: null,
    ultimateReady: false,
    ultimateUsed: false,
    activeUltimate: null,
    cameraShakeUntil: 0,
    // Speech-bubble timestamp — set by onCorrectQuiz when a user has set
    // a catchphrase. Read by the player render to draw the bubble.
    catchphraseUntil: 0,
    catchphraseText: null,  // populated from props each frame
    // Track which cops/FBI have been permanently destroyed by missiles/
    // Knight Hauler signature weapon — a flail (chain + spiked iron ball)
    // that swings behind the car and damages cops/FBI on contact. Only
    // initialised when the player is driving the Knight; for other
    // chassis this stays null and the update/draw helpers no-op.
    knightFlail: null,
    // Oil slicks dropped by the player (Shift+O). Spin nearby cops.
    // {id, x, y, r, createdAt, expiresAt, usedCops: Set<idx>}
    oilSlicks: [],
    oilSlickReadyAt: 0, // cooldown gate — next time the player can deploy
    // monkey-bombs so they can never re-engage. Stored as the cop's index
    // in `enemies` — the cop entry remains in the array but is invisible
    // and ignored by AI/collision/damage from this frame onward.
    destroyedEnemyIdx: new Set(),
  };
}

// Pre-defined boost-gate positions per city. Each gate is a 32×32 px ring
// the player drives through; doing so triggers a 2s tire-glow + 25% speed
// bonus. Positions hand-tuned to sit on each city's racing line.
function makeGatesForCity(cityId) {
  const G = (x, y) => ({ x, y, r: 26, used: false, hitAt: 0 });
  const layouts = {
    paris:  [G(280, 580), G(640, 280), G(1000, 580), G(640, 580)],
    nyc:    [G(640, 200), G(640, 620), G(200, 410), G(1080, 410)],
    london: [G(640, 200), G(640, 620), G(200, 410), G(1080, 410)],
    toronto:[G(640, 200), G(640, 620), G(200, 410), G(1080, 410)],
    tokyo:  [G(220, 320), G(540, 320), G(860, 320), G(220, 580), G(540, 580), G(860, 580)],
    la:     [G(220, 320), G(540, 320), G(860, 320), G(220, 580), G(540, 580), G(860, 580)],
    detroit:[G(220, 320), G(540, 320), G(860, 320), G(220, 580), G(540, 580), G(860, 580)],
    cairo:  [G(180, 410), G(450, 350), G(720, 470), G(990, 380), G(1140, 410)],
    beirut: [G(640, 360), G(640, 600), G(140, 480), G(1140, 480)],
    mexico_city: [G(640, 360), G(640, 600), G(140, 480), G(1140, 480)],
  };
  return layouts[cityId] || [];
}

function allCollected(st) { return st.items.every((i) => i.collected); }

function updatePlayer(st) {
  const p = st.player;
  const now = performance.now();

  // 1) RELEASE first — if jail timer has expired, restore HP + respawn safely.
  //    Must run before the arrest check, otherwise hp<=0 would re-jail the
  //    player on the same frame and they'd be stuck forever.
  if (p.jailedUntil !== 0 && p.jailedUntil <= now) {
    p.hp = p.max_hp;
    p.jailedUntil = 0;
    p.hitsTaken = 0; // reset 3-strike counter after each jail term
    const safe = pickSafeRespawn(st.enemies, st.obstacles);
    p.x = safe.x;
    p.y = safe.y;
    p.vx = 0;
    p.vy = 0;
    // Aim toward map center so the player faces into the action, not a wall
    p.angle = Math.atan2(WORLD_H / 2 - safe.y, WORLD_W / 2 - safe.x);
    p._jailFired = "released";
  }

  // 2) FRESH ARREST — only trigger when HP hits zero and we're not already jailed.
  //    SKIPPED while invulnerable (cop-steal active) — player can't be jailed.
  if (p.hp <= 0 && p.jailedUntil === 0 && !p.invulnerable) {
    p.jailedUntil = now + 15000;
    p.vx = 0; p.vy = 0;
    // Teleport car to top-right "jail cell" corner of the map — this position
    // is fixed so the bird's-eye jail-cell visual (drawn in renderMap) lines
    // up with the player car.
    p.x = WORLD_W - 80;
    p.y = 80;
    p.jailOriginX = p.x;
    p.jailOriginY = p.y;
    sfx.siren?.();
    p._jailFired = "arrested"; // sentinel for the loop to fire onJailChange
  }

  // 3) If still inside the jail window, freeze controls.
  if (p.jailedUntil > now) {
    p.vx = 0; p.vy = 0;
    return;
  }

  // 3b) Carjacked — gang has the player. Movement + weapons are off; the
  //     only input the player has is E to mash the struggle meter
  //     (handled in the keydown listener via gangStruggle). gangs.js
  //     orbits the van around heldX/heldY so the player appears to be
  //     driven around during the theft.
  if (isPlayerStolen(st)) {
    p.vx = 0; p.vy = 0;
    return;
  }

  // 3c) Invulnerable (cop-steal active) — keep HP pinned at max so the
  //     HUD reads "100" the whole time and no slow obstacle drain breaks
  //     the "you can only use this once" power.
  if (p.invulnerable) {
    p.hp = p.max_hp;
    p.hitsTaken = 0;
  }

  const keys = st.keys;
  const speedBuff = st.boostUntil > now ? 2.2 : 1;
  // Gate-boost: 25% speed for 2 seconds after driving through a checker ring.
  // Stacks multiplicatively with the rocket boost.
  const gateBuff = p.gateBoostUntil > now ? 1.25 : 1;
  const accel = 0.22 * (1 + (p.speed_level - 1) * 0.18) * speedBuff * gateBuff;
  const maxSpeed = 5.2 * (1 + (p.speed_level - 1) * 0.18) * (speedBuff > 1 ? 1.9 : 1) * gateBuff;
  const turn = 0.055;
  const drag = 0.96;
  const forward = keys["arrowup"] || keys["w"];
  const backward = keys["arrowdown"] || keys["s"];
  const left = keys["arrowleft"] || keys["a"];
  const right = keys["arrowright"] || keys["d"];

  if (Math.hypot(p.vx, p.vy) > 0.1) {
    if (left) p.angle -= turn;
    if (right) p.angle += turn;
  }
  if (forward) { p.vx += Math.cos(p.angle) * accel; p.vy += Math.sin(p.angle) * accel; }
  else if (backward) { p.vx -= Math.cos(p.angle) * accel * 0.7; p.vy -= Math.sin(p.angle) * accel * 0.7; }
  p.vx *= drag; p.vy *= drag;
  const v = Math.hypot(p.vx, p.vy);
  if (v > maxSpeed) { p.vx = (p.vx / v) * maxSpeed; p.vy = (p.vy / v) * maxSpeed; }

  const nx = p.x + p.vx, ny = p.y + p.vy;
  let blocked = false;
  let inSewer = false;
  for (const o of st.obstacles) {
    if (rectContains(o, nx, ny, 16)) {
      if (o.type === "sewer") {
        // Sewers are a slow-trap: drivable but the car plunges, loses speed
        // and takes a small HP nick. Don't block movement.
        inSewer = true;
        p.hp -= 0.15;
        continue;
      }
      blocked = true;
      p.vx *= -0.55; p.vy *= -0.55;
      p.hp -= o.type === "museum" ? 0.5 : o.type === "construction" ? 0.3 : 0.2;
      break;
    }
  }
  if (!blocked) { p.x = CLAMP(nx, 18, WORLD_W - 18); p.y = CLAMP(ny, 18, WORLD_H - 18); }
  if (inSewer) { p.vx *= 0.78; p.vy *= 0.78; }
  p.hp = CLAMP(p.hp, 0, p.max_hp);

  // Edge-cross detection — if the player drives toward an edge with forward
  // velocity, fire onRouteEdgeCross('right'|'left'). Latch prevents firing
  // every frame. A cooldown after each route swap stops boss-bounces from
  // throwing the player straight back across the opposite edge.
  // While on route 1 with the boss un-solved, the LEFT edge is locked —
  // the player must solve the riddle to move on, not retreat.
  const cooldownActive = (st._edgeCrossCooldownUntil ?? 0) > now;
  const bossLocksLeft = st._riddleRouteIndex >= 1 && !st._riddleSolved;
  if (!st._edgeCrossLatch && !cooldownActive && p.jailedUntil <= now) {
    if (p.x >= WORLD_W - 22 && p.vx > 1.5) {
      st._edgeCrossLatch = true;
      st._edgeCrossDir = "right";
    } else if (p.x <= 22 && p.vx < -1.5 && !bossLocksLeft) {
      st._edgeCrossLatch = true;
      st._edgeCrossDir = "left";
    }
  }

  // Boss zone — block the player from continuing past the boss until they
  // solve the riddle. Triggers the modal every frame while in zone (handler
  // is idempotent — opening an already-open modal is a no-op). Active on
  // BOTH route 1 (first boss) and route 2 (championship boss).
  if (st._riddleRouteIndex >= 1 && !st._riddleSolved) {
    const bdx = p.x - BOSS_X;
    const bdy = p.y - BOSS_Y;
    const bd = Math.hypot(bdx, bdy);
    if (bd < 70) {
      // Bounce the player back from the boss (gentle bounce — we don't want
      // the player to fly into the opposite edge and trigger another route swap)
      const nx = bdx / (bd || 1), ny = bdy / (bd || 1);
      p.vx = nx * 2;
      p.vy = ny * 2;
      p.x = BOSS_X + nx * 72;
      p.y = BOSS_Y + ny * 72;
      // Fire the encounter every frame while in zone — handler is idempotent
      // and skips if riddle data isn't loaded yet (LLM still in flight).
      if (st._onBossEncounter) st._onBossEncounter();
    }
  }

  // Speed-gate ring check — if the player drives through an unused gate,
  // grant a 2-second tire-glow boost.
  for (const g of st.gates) {
    if (g.used) continue;
    const d = Math.hypot(g.x - p.x, g.y - p.y);
    if (d < g.r) {
      g.used = true;
      g.hitAt = now;
      p.gateBoostUntil = now + 6000;
      sfx.pickup?.();
    }
  }
}
function rectContains(o, x, y, pad = 0) {
  return x > o.x - pad && x < o.x + o.w + pad && y > o.y - pad && y < o.y + o.h + pad;
}
// Pick the respawn point (from a grid of candidates) that is farthest from
// any cop/FBI AND clear of obstacles. Ensures post-jail players don't
// immediately get re-tackled by the nearest pursuer.
function pickSafeRespawn(enemies, obstacles) {
  const candidates = [
    { x: 120, y: WORLD_H - 120 },           // bottom-left (original start)
    { x: WORLD_W - 120, y: WORLD_H - 120 }, // bottom-right
    { x: 120, y: 120 },                     // top-left
    { x: WORLD_W / 2, y: WORLD_H - 120 },   // bottom-center
    { x: 120, y: WORLD_H / 2 },             // mid-left
    { x: WORLD_W - 120, y: WORLD_H / 2 },   // mid-right
    { x: WORLD_W / 2, y: 120 },             // top-center
  ];
  let best = candidates[0];
  let bestScore = -Infinity;
  for (const c of candidates) {
    // Reject spots that land inside an obstacle
    if (obstacles.some((o) => rectContains(o, c.x, c.y, 24))) continue;
    // Score = distance to the closest enemy (bigger = safer)
    let minD = Infinity;
    for (const e of enemies) {
      const d = Math.hypot(e.x - c.x, e.y - c.y);
      if (d < minD) minD = d;
    }
    if (minD > bestScore) { bestScore = minD; best = c; }
  }
  return best;
}
function updateMissiles(st, sendEvent) {
  const alive = [];
  for (const m of st.missiles) {
    m.x += m.vx; m.y += m.vy; m.life -= 1;
    if (m.life <= 0 || m.x < 0 || m.x > WORLD_W || m.y < 0 || m.y > WORLD_H) continue;
    let hit = false;
    for (const o of st.obstacles) { if (rectContains(o, m.x, m.y)) { hit = true; break; } }
    for (let i = 0; i < st.enemies.length; i++) {
      const e = st.enemies[i];
      if (st.destroyedEnemyIdx.has(i)) continue;
      if (e.disabled > performance.now()) continue;
      if (Math.hypot(e.x - m.x, e.y - m.y) < 22) {
        const a = Math.atan2(m.vy, m.vx);
        e.x += Math.cos(a) * 30; e.y += Math.sin(a) * 30;
        e.hp -= e.type === "fbi" ? 35 : 50;
        if (e.hp <= 0) {
          // PERMANENT KILL — explosion VFX + remove from track for the
          // remainder of the race (no re-spawn).
          spawnExplosion(st, e.x, e.y, e.type === "fbi" ? "#3070FF" : "#FF3030");
          st.destroyedEnemyIdx.add(i);
        }
        hit = true; break;
      }
    }
    if (!hit) alive.push(m);
  }
  st.missiles = alive;
  const rAlive = [];
  for (const m of st.remoteMissiles) {
    m.x += m.vx; m.y += m.vy; m.life -= 1;
    if (m.life <= 0 || m.x < 0 || m.x > WORLD_W || m.y < 0 || m.y > WORLD_H) continue;
    const p = st.player;
    if (Math.hypot(p.x - m.x, p.y - m.y) < 22) {
      sfx.hit();
      sendEvent?.({ type: "took_damage", amount: 12, attacker_id: m.attacker_id });
      continue;
    }
    rAlive.push(m);
  }
  st.remoteMissiles = rAlive;
}
function updateMonkeys(st, sendEvent) {
  const alive = [];
  for (const m of st.monkeys) {
    m.life -= 1; m.r = m.r + (m.maxR - m.r) * 0.15;
    // AoE damage to enemies once per blast.
    if (!m._enemiesHit) {
      m._enemiesHit = new Set();
    }
    for (let i = 0; i < st.enemies.length; i++) {
      if (m._enemiesHit.has(i)) continue;
      if (st.destroyedEnemyIdx.has(i)) continue;
      const e = st.enemies[i];
      if (e.disabled > performance.now()) continue;
      const d = Math.hypot(e.x - m.x, e.y - m.y);
      if (d < m.r) {
        m._enemiesHit.add(i);
        e.hp -= e.type === "fbi" ? 30 : 60;
        // Knock back hard
        const ax = e.x - m.x, ay = e.y - m.y;
        const dn = Math.hypot(ax, ay) || 1;
        e.x += (ax / dn) * 40; e.y += (ay / dn) * 40;
        if (e.hp <= 0) {
          spawnExplosion(st, e.x, e.y, e.type === "fbi" ? "#3070FF" : "#FF3030");
          st.destroyedEnemyIdx.add(i);
        }
      }
    }
    if (m.life > 0) alive.push(m);
  }
  st.monkeys = alive;
  const rAlive = [];
  for (const m of st.remoteMonkeys) {
    m.life -= 1; m.r = m.r + (m.maxR - m.r) * 0.15;
    const p = st.player;
    if (m.life > 0 && Math.hypot(p.x - m.x, p.y - m.y) < m.r) {
      if (!m._hit) {
        m._hit = true;
        sfx.hit();
        sendEvent?.({ type: "took_damage", amount: 18, attacker_id: m.attacker_id });
      }
    }
    if (m.life > 0) rAlive.push(m);
  }
  st.remoteMonkeys = rAlive;
}
// Tick oil slicks dropped by the player. Each slick stays on the road for
// a fixed window and spins ANY cop/FBI that touches it (one spinout per
// cop per slick — they re-engage afterward).
// Render every active oil slick. Slicks glow a cool oil-rainbow then
// fade as their `expiresAt` approaches.
function drawOilSlicks(ctx, st) {
  if (!st.oilSlicks) return;
  const now = performance.now();
  for (const slick of st.oilSlicks) {
    const life = (slick.expiresAt - now) / (slick.expiresAt - slick.createdAt);
    const alpha = Math.max(0, Math.min(1, life)) * 0.85;
    ctx.save();
    // Outer halo
    ctx.globalAlpha = alpha * 0.5;
    ctx.fillStyle = "#0a0a0a";
    ctx.beginPath();
    ctx.arc(slick.x, slick.y, slick.r, 0, Math.PI * 2);
    ctx.fill();
    // Inner pool — slightly darker
    ctx.globalAlpha = alpha;
    ctx.fillStyle = "#1a1015";
    ctx.beginPath();
    ctx.arc(slick.x, slick.y, slick.r * 0.78, 0, Math.PI * 2);
    ctx.fill();
    // Oil-rainbow sheen rings
    ctx.globalAlpha = alpha * 0.45;
    ctx.strokeStyle = "#FFCC00";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(slick.x, slick.y, slick.r * 0.55, 0, Math.PI * 2);
    ctx.stroke();
    ctx.strokeStyle = "#34C759";
    ctx.beginPath();
    ctx.arc(slick.x, slick.y, slick.r * 0.32, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }
}

function updateOilSlicks(st) {
  const now = performance.now();
  if (!st.oilSlicks || st.oilSlicks.length === 0) return;
  // Drop expired slicks.
  st.oilSlicks = st.oilSlicks.filter((s) => now < s.expiresAt);
  for (const slick of st.oilSlicks) {
    for (let i = 0; i < st.enemies.length; i++) {
      if (st.destroyedEnemyIdx.has(i)) continue;
      if (slick.usedCops.has(i)) continue;
      const e = st.enemies[i];
      const d = Math.hypot(slick.x - e.x, slick.y - e.y);
      if (d <= slick.r) {
        // Spin for 3 seconds. updateEnemies sees e.spinUntil and replaces
        // chase logic with a fast in-place rotation + velocity decay.
        e.spinUntil = Math.max(e.spinUntil || 0, now + 3000);
        // Give the cop an initial tangential nudge so the spin looks
        // chaotic instead of a stationary pirouette.
        e.vx = (Math.random() - 0.5) * 4;
        e.vy = (Math.random() - 0.5) * 4;
        slick.usedCops.add(i);
      }
    }
  }
}

function updateEnemies(st, graceOver, speedMult) {
  const p = st.player;
  const now = performance.now();
  // Leash distance — once a cop has fallen this far behind, they "lose the
  // trail" and idle (slow drift) until the player is closer again. Lets a
  // good driver actually escape and breathe instead of cops snapping back
  // every frame. Player feedback: "I should be able to out race the cops
  // and not see them for awhile."
  const LEASH_R = 380;
  for (let i = 0; i < st.enemies.length; i++) {
    if (st.destroyedEnemyIdx.has(i)) continue;  // permanently dead
    const e = st.enemies[i];

    // Disabled (destroyed) enemies coast to a stop with smoke. They will
    // re-engage once the disable timer expires.
    if (e.disabled > now) {
      e.vx *= 0.85; e.vy *= 0.85;
      e.x += e.vx; e.y += e.vy;
      continue;
    }

    if (!graceOver) { e.vx *= 0.9; e.vy *= 0.9; e.x += e.vx; e.y += e.vy; continue; }

    // Spinout (oil slick) — cop is helpless: spin in place with no chase
    // logic until the timer expires. e.spinUntil set in deployOilSlick().
    if ((e.spinUntil || 0) > now) {
      e.angle = (e.angle || 0) + 0.45;
      e.vx *= 0.9; e.vy *= 0.9;
      e.x += e.vx; e.y += e.vy;
      continue;
    }

    // Leash check — beyond LEASH_R cops slow drift toward the player at
    // half-speed instead of full sprint. They re-engage at full chase
    // speed once the player closes back inside the leash.
    const distToPlayer = Math.hypot(p.x - e.x, p.y - e.y);
    const outsideLeash = distToPlayer > LEASH_R;

    // Tactical cornering — each cop aims for a unique offset around the
    // player so the pack surrounds rather than stacking up. The targeted
    // position rotates slowly so the player can't outrun a static formation.
    const ringR = 70;
    const phase = now / 1200;
    const targetX = p.x + Math.cos(e.cornerOffset + phase) * ringR;
    const targetY = p.y + Math.sin(e.cornerOffset + phase) * ringR;
    let ax = targetX - e.x, ay = targetY - e.y;
    let d = Math.hypot(ax, ay) || 1;
    // Outside the leash → cops drift at half-speed, not full chase. Lets
    // the player put real distance between themselves and the cops if
    // they're driving cleanly.
    const sp = (e.type === "fbi" ? 1.9 : 1.6) * speedMult * (outsideLeash ? 0.45 : 1.0);
    e.vx = (ax / d) * sp; e.vy = (ay / d) * sp;

    // Cop-vs-cop separation — push out of any other live enemy within 40 px
    // so they don't collapse into one tile.
    for (let j = 0; j < st.enemies.length; j++) {
      if (j === i) continue;
      if (st.destroyedEnemyIdx.has(j)) continue;
      const other = st.enemies[j];
      if (other.disabled > now) continue;
      const ox = e.x - other.x, oy = e.y - other.y;
      const od = Math.hypot(ox, oy);
      if (od < 36 && od > 0) {
        e.vx += (ox / od) * 0.8;
        e.vy += (oy / od) * 0.8;
      }
    }

    e.x += e.vx; e.y += e.vy;
    e.angle = Math.atan2(p.y - e.y, p.x - e.x);

    // BOUNCE OFF the player + register a hit. Cooldown prevents spam-damage
    // every frame the cars overlap.
    // --- GATE-BOOST PHASE-THROUGH ---------------------------------------
    // While the player's blue power-burst (gate boost) is active the car
    // is intangible to cops + FBI: we skip the damage/bounce block, but
    // still shove enemies out of the way so they don't stack on top of
    // the player.
    const dx = p.x - e.x, dy = p.y - e.y;
    const dp = Math.hypot(dx, dy);
    const phasing = p.gateBoostUntil > now;
    if (dp < 28) {
      if (phasing || p.invulnerable) {
        // Flick the cop sideways — visual satisfaction, zero damage.
        const nx = dx / (dp || 1), ny = dy / (dp || 1);
        e.vx -= nx * 7; e.vy -= ny * 7;
        e.x -= nx * 10; e.y -= ny * 10;
        e.disabled = Math.max(e.disabled, now + 400); // brief stun
        continue;
      }
      // Symmetric bounce — cop AND player both shoved outward.
      const nx = dx / (dp || 1), ny = dy / (dp || 1);
      const knock = 5;
      e.vx -= nx * knock; e.vy -= ny * knock;
      e.x -= nx * 6; e.y -= ny * 6;
      p.vx += nx * knock * 0.9; p.vy += ny * knock * 0.9;
      // Damage + hit-counter once per cooldown
      if (e.bounceCooldown < now) {
        e.bounceCooldown = now + 600;
        p.hp -= e.type === "fbi" ? 0.9 : 0.55;
        // Three-strike rule: 3 cop rams sends you straight to jail even if
        // HP isn't depleted yet. Resets after each jail/respawn.
        p.hitsTaken += 1;
        p.hitFlashUntil = now + 250;
        if (p.hitsTaken >= 3 && p.jailedUntil === 0) {
          p.hp = 0; // triggers arrest in updatePlayer next frame
        }
      }
    }
  }
}

// A-Team van rescue: triggered from Race.jsx after 2 correct quiz answers.
// The van enters from the player's left edge, drives toward the player,
// then ploughs through any cops within 50 px, knocking them aside + stunning
// them for 6 s. After traversing the screen it exits the right edge.
function checkItemPickup(st, onItemTouched) {
  if (st.pickupLocked) return;
  const p = st.player;
  const COOLDOWN_EXIT = ARTIFACT_RADIUS + 28; // distance you must walk out to before retry
  for (const it of st.items) {
    if (it.collected) continue;
    const d = Math.hypot(it.x - p.x, it.y - p.y);
    // Clear the cooldown once the player has driven out of the pickup zone.
    if (it.cooldown && d > COOLDOWN_EXIT) it.cooldown = false;
    if (it.cooldown) continue;
    if (d < ARTIFACT_RADIUS + 6) {
      st.pickupLocked = true;
      onItemTouched?.(it);
      break;
    }
  }
}

// ======================================================================================
// Jail overlay — cartoon jail cell + countdown
// ======================================================================================
// --- Edge route portal ----------------------------------------------------
// Animated chevron + label at the left/right edge of the world hinting the
// player can drive off-screen to access a new route.
function drawRoutePortal(ctx, side) {
  const x = side === "right" ? 1280 - 24 : 24;
  const y = 820 / 2;
  const dirX = side === "right" ? 1 : -1;
  const t = Math.floor(performance.now() / 250) % 4;
  ctx.save();
  // Backing column
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  ctx.fillRect(x - 14, y - 60, 28, 120);
  ctx.strokeStyle = "#FFCC00";
  ctx.lineWidth = 2;
  ctx.strokeRect(x - 14, y - 60, 28, 120);
  // Three pulsing chevrons pointing toward the edge
  for (let i = 0; i < 3; i++) {
    const alpha = i === t % 3 ? 1 : 0.35;
    ctx.fillStyle = `rgba(255, 204, 0, ${alpha})`;
    ctx.beginPath();
    ctx.moveTo(x + dirX * (-4 + i * 4), y - 14);
    ctx.lineTo(x + dirX * (4 + i * 4), y);
    ctx.lineTo(x + dirX * (-4 + i * 4), y + 14);
    ctx.closePath();
    ctx.fill();
  }
  // Label rotated 90°
  ctx.save();
  ctx.translate(x, y - 80);
  ctx.rotate(-Math.PI / 2);
  ctx.fillStyle = "#FFCC00";
  ctx.font = "bold 9px 'Press Start 2P', monospace";
  ctx.textAlign = "center";
  ctx.fillText(side === "right" ? "NEXT DISTRICT" : "BACK", 0, 0);
  ctx.restore();
  ctx.restore();
}

// --- Persistent police-station marker + jail overlay now live in
// components/game/jail.js (extracted for maintainability).

function renderMap(ctx, st, remote, car, theme, difficulty, skylineImg, camCX) {
  drawSky(ctx, theme, skylineImg, camCX);
  drawGrass(ctx, st.items, st.cityId);
  drawRoads(ctx, theme, st.cityId);

  // Per-city pixel-art buildings lining the main road cross (Haussmann
  // stone for Paris, red-brick brownstones for London, glass condos for
  // Toronto, etc.). Drawn over the grass/pavement but before landmarks
  // and collectibles.
  if (st.cityId) drawCityGroundBuildings(ctx, st.cityId);
  // Giant pixel-art city landmark (Eiffel, Pyramid, Big Ben, etc.) drawn on
  // top of roads so it's a clear centerpiece. Drawn before items/cars so
  // those layer above it.
  if (st.cityId) drawCityLandmark(ctx, st.cityId);

  for (const o of st.obstacles) drawObstacle(ctx, o);

  // Per-city pixel-art flavor props (palm trees for LA, phone boxes for
  // London, yellow cabs for NYC, papel-picado for Mexico City, etc.) —
  // drawn AFTER obstacles so they sit on top of the ground, but BEFORE
  // artifacts so collectible icons remain the top visual priority.
  if (st.cityId) drawCityProps(ctx, st.cityId);

  // Speed-boost gates (Sonic-style checker rings) — drawn AFTER roads but
  // BEFORE artifacts so they sit on the track surface
  for (const g of st.gates) drawBoostGate(ctx, g);

  // artifacts (BIG)
  for (const it of st.items) {
    if (it.collected) continue;
    drawArtifact(ctx, it, { cityId: st.cityId });
  }

  // pedestrians
  for (const ped of st.pedestrians.list) drawPedestrian(ctx, ped);

  // monkey blasts
  [...st.monkeys, ...st.remoteMonkeys].forEach((m) => drawMonkeyBlast(ctx, m));

  // missiles
  for (const m of st.missiles)       drawMissile(ctx, m, "#FFD166", "#E63946");
  for (const m of st.remoteMissiles) drawMissile(ctx, m, "#E63946", "#FFD166");

  // Oil slicks dropped by the player — rendered BELOW enemies so cops
  // visibly slide across them. Each slick is a glossy black puddle with
  // a fading rainbow sheen as it ages.
  drawOilSlicks(ctx, st);

  // enemies — skip permanently-destroyed ones
  for (let i = 0; i < st.enemies.length; i++) {
    if (st.destroyedEnemyIdx.has(i)) continue;
    drawEnemy(ctx, st.enemies[i]);
  }

  // Explosion VFX (fire ring + smoke + debris) for missile/monkey kills
  drawExplosions(ctx, st);

  // Pixel-art boss — only visible on route 1 + not yet solved
  drawRiddleBoss(ctx, st);

  // Edge route portals — chevron arrows at each side of the world hinting
  // the player can drive off-edge into a new district.
  drawRoutePortal(ctx, "right");
  drawRoutePortal(ctx, "left");

  // A-Team van (rescue vehicle that crashes through cops after 2 correct quizzes)
  if (st.aTeamVan) drawATeamVan(ctx, st.aTeamVan);

  // Carjacker gang van — chases on screen ~25 s into the race, freezes the
  // player on contact, peels off when they mash E to escape.
  drawGangVan(ctx, st);

  // Persistent jail-cell marker at top-right of the world so the player
  // can see where they'll be sent if arrested.
  drawJailCellMarker(ctx, WORLD_W - 80, 80);

  // remote players
  for (const [uid, r] of Object.entries(remote)) {
    drawCar(ctx, r.x, r.y, r.angle, r.body_color ?? "#4A90E2", r.accent_color ?? "#FFFFFF", 0.9, r.kind || "cruiser");
    drawNameTag(ctx, r.x, r.y - 42, (r.name ?? "RACER").toUpperCase());
    const hp = st.remoteHp[uid];
    if (hp !== undefined) drawHpBar(ctx, r.x, r.y - 28, hp / 100);
  }

  // player: dust puffs behind when moving fast
  const p = st.player;
  const sp = Math.hypot(p.vx, p.vy);
  if (sp > 2.5) drawDustPuffs(ctx, p);
  // Cosmetic exhaust trail — appended when the player has equipped one.
  if (car.active_trail_color && sp > 1.5) drawCosmeticTrail(ctx, p, car.active_trail_color);
  // TITAN MODE — temporarily scale the player car. Implemented as an alpha
  // override on drawCar so the cruiser/tank/etc. all grow without needing
  // a per-chassis re-implementation.
  const titan = p._titanScale || 1;
  if (p.copStolen) {
    // Cop-steal active — render as a cop cruiser instead of the user's car.
    drawStolenCopCar(ctx, p);
  } else if (titan !== 1) {
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.scale(titan, titan);
    drawCar(ctx, 0, 0, p.angle, car.body_color, car.accent_color, 1, car.kind || "cruiser");
    ctx.restore();
  } else {
    drawCar(ctx, p.x, p.y, p.angle, car.body_color, car.accent_color, 1, car.kind || "cruiser");
  }
  // INVINCIBLE pulsing badge over the player while cop-steal is active.
  drawInvincibleBadge(ctx, p);
  // Knight Hauler flail — chain + spiked ball trailing the car. Drawn
  // AFTER the car so the chain reads "anchored to the rear bumper" and
  // the ball passes over enemies it strikes.
  if ((car.kind || "cruiser") === "knight" && !p.copStolen) drawKnightFlail(ctx, st);
  // Catchphrase speech bubble (when active) — read from state ref so this
  // function never closes over a stale prop reference.
  if (st.catchphraseText && st.catchphraseUntil > performance.now()) {
    drawCatchphrase(ctx, p.x, p.y, st.catchphraseText, st.catchphraseUntil);
  }
  // Sidekick wingman — draw underneath the player so the player car stays
  // most-prominent. Permanent until end of race or until sidekick HP <= 0.
  if (st.sidekick && st.sidekick.hp > 0) drawSidekick(ctx, st.sidekick);

  // ULTIMATE effects (wheel projectiles, spike rings, hulk fist, flames…)
  drawActiveUltimate(ctx, st);
  // Gate-boost: glowing tires + phase-through aura (blue energy shield)
  if (p.gateBoostUntil > performance.now()) {
    drawGlowingTires(ctx, p);
    drawPhaseAura(ctx, p, p.gateBoostUntil);
  }
  if (st.boostUntil > performance.now()) drawBoostFlames(ctx, p);

  drawMood(ctx, theme);

  // (Jail overlay is drawn at the canvas-pixel HUD layer in the main loop,
  // not here, so it sits on top of the minimap and other overlays.)

  // grace banner
  const elapsed = (performance.now() - st.startedAt) / 1000;
  const grace = difficulty.grace_seconds ?? 5;
  if (elapsed < grace) drawGraceBanner(ctx, Math.ceil(grace - elapsed));

  // View badge
  ctx.save();
  ctx.fillStyle = "rgba(255,209,102,0.9)";
  ctx.strokeStyle = "#0A0A0A";
  ctx.lineWidth = 2;
  ctx.beginPath();
  const x = 14, y = 14, w = 110, h = 26;
  ctx.moveTo(x + 4, y);
  ctx.lineTo(x + w - 4, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + 4);
  ctx.lineTo(x + w, y + h - 4);
  ctx.quadraticCurveTo(x + w, y + h, x + w - 4, y + h);
  ctx.lineTo(x + 4, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - 4);
  ctx.lineTo(x, y + 4);
  ctx.quadraticCurveTo(x, y, x + 4, y);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = "#0A0A0A";
  ctx.font = "bold 14px 'Anton'";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText("MAP", 22, 28);
  ctx.restore();
}


// ---- Speed-boost gate (Sonic-style checker ring) ---------------------
function drawBoostGate(ctx, g) {
  const now = performance.now();
  // Gates fade out for 600ms after being hit, then disappear.
  if (g.used && now - g.hitAt > 600) return;
  ctx.save();
  // Pulsing scale so the rings catch the eye on the track.
  const t = now / 200;
  const pulse = 1 + Math.sin(t + g.x * 0.01) * 0.08;
  let alpha = 1;
  let scale = pulse;
  if (g.used) {
    const fade = (now - g.hitAt) / 600;
    alpha = 1 - fade;
    scale = pulse * (1 + fade * 1.4); // ring expands as it fades
  }
  ctx.globalAlpha = alpha;
  ctx.translate(g.x, g.y);
  ctx.scale(scale, scale);
  // Outer ring (yellow + thick black border) — chunky pixel feel
  const r = g.r;
  ctx.lineWidth = 6;
  ctx.strokeStyle = "#000";
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.lineWidth = 4;
  ctx.strokeStyle = "#FFCC00";
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.stroke();
  // Checker pattern around the ring perimeter (8 segments, alternating)
  const segs = 8;
  for (let i = 0; i < segs; i++) {
    const a0 = (i / segs) * Math.PI * 2;
    const a1 = ((i + 1) / segs) * Math.PI * 2;
    ctx.beginPath();
    ctx.arc(0, 0, r, a0, a1);
    ctx.lineWidth = 6;
    ctx.strokeStyle = i % 2 === 0 ? "#FFFFFF" : "#0A0A0A";
    ctx.stroke();
  }
  // Inner glow disk
  const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, r - 4);
  grad.addColorStop(0, "rgba(255, 220, 80, 0.55)");
  grad.addColorStop(1, "rgba(255, 220, 80, 0)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(0, 0, r - 4, 0, Math.PI * 2);
  ctx.fill();
  // Tiny sparkle dots
  for (let i = 0; i < 6; i++) {
    const a = t * 0.4 + i * (Math.PI / 3);
    const sx = Math.cos(a) * (r - 8);
    const sy = Math.sin(a) * (r - 8);
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(sx - 1.5, sy - 1.5, 3, 3);
  }
  ctx.restore();
}

// ---- Cosmetic exhaust trail (purchased in Garage) ---------------------
// Colored streak behind the player car. Supports `__rainbow__` sentinel for
// the premium Rainbow Trail cosmetic.
function drawCosmeticTrail(ctx, p, color) {
  const rainbow = color === "__rainbow__";
  const palette = rainbow
    ? ["#FF3B30", "#FFCC00", "#34C759", "#00F0FF", "#7B2CBF"]
    : null;
  const spd = Math.hypot(p.vx, p.vy);
  const len = Math.min(80, 14 + spd * 6);
  const steps = 10;
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.angle);
  for (let i = 0; i < steps; i++) {
    const t = i / steps;
    const x = -18 - t * len;
    const thickness = 6 * (1 - t) + 2;
    const alpha = (1 - t) * 0.75;
    ctx.fillStyle = rainbow ? palette[i % palette.length] : color;
    ctx.globalAlpha = alpha;
    ctx.fillRect(x, -6 + Math.sin(performance.now() / 80 + i) * 1.5, 10, thickness);
  }
  ctx.restore();
  ctx.globalAlpha = 1;
}


// ---- Catchphrase speech bubble (pops over the player on correct quiz) ----
function drawCatchphrase(ctx, px, py, text, until) {
  const remaining = until - performance.now();
  const fade = remaining < 500 ? Math.max(0, remaining / 500) : 1;
  const t = (until - performance.now()) / 2500;
  const yOffset = -42 - (1 - t) * 6; // float up slightly
  ctx.save();
  ctx.globalAlpha = fade;
  ctx.font = "bold 11px 'Press Start 2P', monospace";
  ctx.textAlign = "center";
  const metrics = ctx.measureText(text);
  const w = Math.min(220, metrics.width + 16);
  const h = 22;
  const x = px - w / 2;
  const y = py + yOffset - h;
  // Bubble
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 2;
  ctx.strokeRect(x, y, w, h);
  // Tail
  ctx.beginPath();
  ctx.moveTo(px - 6, y + h);
  ctx.lineTo(px, y + h + 8);
  ctx.lineTo(px + 6, y + h);
  ctx.closePath();
  ctx.fillStyle = "#FFFFFF";
  ctx.fill();
  ctx.stroke();
  // Text
  ctx.fillStyle = "#000";
  ctx.textBaseline = "middle";
  // Trim if too long
  let display = text;
  if (metrics.width > 200) {
    const cap = Math.floor(200 / 7);
    display = text.slice(0, cap);
  }
  ctx.fillText(display, px, y + h / 2 + 1);
  ctx.restore();
}


// ---- Permanent destruction explosion (missile/monkey kill) ------------
// Spawns a burst of particles: orange flames, dark smoke, white sparks,
// flying debris. Each particle dies in ~90 frames; the wreck is left as
// a static blackened/scorched marker until the race ends.
function spawnExplosion(st, x, y, accentColor = "#FF3030") {
  const particles = [];
  // Orange/yellow flame particles
  for (let i = 0; i < 22; i++) {
    const a = Math.random() * Math.PI * 2;
    const sp = 1.5 + Math.random() * 4;
    particles.push({
      x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
      r: 6 + Math.random() * 6, life: 40 + Math.random() * 25,
      type: "flame",
      hue: i % 3 === 0 ? "#FFD166" : i % 3 === 1 ? "#FF7B00" : "#E63946",
    });
  }
  // Dark smoke (rises slowly)
  for (let i = 0; i < 14; i++) {
    const a = Math.random() * Math.PI * 2;
    particles.push({
      x: x + Math.cos(a) * 4, y: y + Math.sin(a) * 4,
      vx: Math.cos(a) * 0.6, vy: -0.8 - Math.random() * 0.5,
      r: 5 + Math.random() * 5, life: 60 + Math.random() * 40,
      type: "smoke",
      hue: "#2a2a2a",
    });
  }
  // Flying metal debris (fast, gravity)
  for (let i = 0; i < 8; i++) {
    const a = Math.random() * Math.PI * 2;
    const sp = 3 + Math.random() * 4;
    particles.push({
      x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 1.5,
      r: 2 + Math.random() * 2, life: 35 + Math.random() * 15,
      type: "debris",
      hue: "#9c98a4",
    });
  }
  st.explosions.push({
    x, y, age: 0, particles, accentColor, wreckage: true,
  });
}

function drawExplosions(ctx, st) {
  const alive = [];
  for (const ex of st.explosions) {
    ex.age += 1;
    // Persistent scorch mark stays for the rest of the race so the wreck
    // is visible in-world.
    if (ex.wreckage) {
      // Black scorch + dim red embers
      ctx.fillStyle = "rgba(15, 13, 18, 0.85)";
      ctx.beginPath();
      ctx.arc(ex.x, ex.y, 18, 0, Math.PI * 2);
      ctx.fill();
      // Twisted metal chunks — three small dark rectangles
      ctx.fillStyle = "#3a3340";
      ctx.fillRect(ex.x - 14, ex.y - 4, 10, 4);
      ctx.fillRect(ex.x - 2, ex.y - 8, 8, 6);
      ctx.fillRect(ex.x + 4, ex.y + 2, 10, 5);
      // Faint red ember pulse
      const pulse = 0.4 + 0.3 * Math.sin(performance.now() / 220 + ex.x * 0.01);
      ctx.fillStyle = `rgba(255, 60, 30, ${pulse * 0.7})`;
      ctx.fillRect(ex.x - 3, ex.y - 1, 6, 3);
    }
    // Live particles
    let liveCount = 0;
    for (const p of ex.particles) {
      if (p.life <= 0) continue;
      p.life -= 1;
      p.x += p.vx; p.y += p.vy;
      if (p.type === "smoke") { p.vy -= 0.02; p.r += 0.1; }
      if (p.type === "debris") { p.vy += 0.18; }
      if (p.type === "flame") { p.r *= 0.96; p.vx *= 0.92; p.vy *= 0.92; }
      const fade = Math.max(0, p.life / 80);
      ctx.fillStyle = p.hue;
      ctx.globalAlpha = fade;
      if (p.type === "flame") {
        // Concentric jagged rect for chunky pixel-art flame
        ctx.fillRect(p.x - p.r, p.y - p.r, p.r * 2, p.r * 2);
        ctx.fillStyle = "#FFE39A";
        ctx.fillRect(p.x - p.r / 2, p.y - p.r / 2, p.r, p.r);
        ctx.fillStyle = p.hue;
      } else if (p.type === "smoke") {
        ctx.fillRect(p.x - p.r, p.y - p.r, p.r * 2, p.r * 2);
      } else {
        ctx.fillRect(p.x - p.r, p.y - p.r, p.r * 2, p.r * 2);
      }
      liveCount += 1;
    }
    ctx.globalAlpha = 1;
    // Initial flash (first 4 frames) — bright accent ring
    if (ex.age < 5) {
      const r = 22 + ex.age * 8;
      ctx.strokeStyle = ex.accentColor;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(ex.x, ex.y, r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(ex.x, ex.y, r - 6, 0, Math.PI * 2);
      ctx.stroke();
    }
    // Keep the wreckage entry for the rest of the race (wreckage flag),
    // even if all particles have died — drawn as a static scorch.
    alive.push(ex);
  }
  st.explosions = alive;
}


// ---- Phase-through aura (blue energy shield while gate-boost is active)
// Renders an expanding/contracting ring around the player so the
// intangibility is obvious. Flickers a second offset ring for that
// retro arcade shield feel.
function drawPhaseAura(ctx, p, gateBoostUntil) {
  const now = performance.now();
  const remaining = gateBoostUntil - now;
  const fade = remaining < 700 ? Math.max(0, remaining / 700) : 1;
  const pulse = 0.5 + 0.5 * Math.sin(now / 90);
  const r = 30 + pulse * 6;
  ctx.save();
  // Outer cyan ring
  ctx.globalAlpha = 0.85 * fade;
  ctx.strokeStyle = "#00F0FF";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
  ctx.stroke();
  // Inner soft-fill
  ctx.globalAlpha = 0.18 * fade;
  ctx.fillStyle = "#4DD0FF";
  ctx.beginPath();
  ctx.arc(p.x, p.y, r - 3, 0, Math.PI * 2);
  ctx.fill();
  // Secondary flicker ring
  ctx.globalAlpha = 0.55 * fade;
  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = 1.5;
  const r2 = r - 4 + Math.sin(now / 55) * 2;
  ctx.beginPath();
  ctx.arc(p.x, p.y, r2, 0, Math.PI * 2);
  ctx.stroke();
  // Four cardinal energy spokes
  ctx.strokeStyle = "#00F0FF";
  ctx.lineWidth = 2;
  ctx.globalAlpha = 0.7 * fade;
  for (let i = 0; i < 4; i++) {
    const a = i * Math.PI / 2 + now / 400;
    const x1 = p.x + Math.cos(a) * (r + 4);
    const y1 = p.y + Math.sin(a) * (r + 4);
    const x2 = p.x + Math.cos(a) * (r + 10);
    const y2 = p.y + Math.sin(a) * (r + 10);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
  ctx.restore();
  ctx.globalAlpha = 1;
}



// ---- Glowing tires while gate-boost is active -------------------------
function drawGlowingTires(ctx, p) {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.angle);
  const flash = (Math.sin(performance.now() / 60) + 1) / 2; // 0..1 strobe
  // Four glowing tire pucks at the corners of the car
  const pts = [
    { x: -16, y: -14 },
    { x: 16,  y: -14 },
    { x: -16, y: 14 },
    { x: 16,  y: 14 },
  ];
  for (const pt of pts) {
    const grad = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, 16);
    grad.addColorStop(0, `rgba(0, 220, 255, ${0.85 + flash * 0.15})`);
    grad.addColorStop(0.5, `rgba(80, 0, 255, ${0.55 + flash * 0.2})`);
    grad.addColorStop(1, "rgba(80, 0, 255, 0)");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(pt.x, pt.y, 16, 0, Math.PI * 2);
    ctx.fill();
  }
  // Trail streak behind each rear tire
  const trail = ctx.createLinearGradient(-30, 0, -8, 0);
  trail.addColorStop(0, "rgba(0, 220, 255, 0)");
  trail.addColorStop(1, "rgba(0, 220, 255, 0.7)");
  ctx.fillStyle = trail;
  ctx.fillRect(-30, -16, 22, 4);
  ctx.fillRect(-30, 12, 22, 4);
  ctx.restore();
}



// Minimap HUD extracted to components/game/minimap.js
